import { MenuDivider } from "@blueprintjs/core"
import { difference, get } from "lodash-es"
import dayjs from "dayjs"
import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import CustomDateRange from "components/Atoms/CustomDateRange"
import CustomTag from "components/Atoms/CustomTag"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import ActionMenu from "components/Organisms/ActionMenu"
import ActionMenuItem from "components/Organisms/ActionMenuItem"
import ReportDialog from "components/Templates/ReportDialog"
import Links from "constants/links"
import { DEPOTS, MARKETS } from "graphql/selector.queries.gql"
import { useAuthorizationModules } from "hooks/useAuth"
import useRedirect from "hooks/useRedirect"
import { CLEAR_SELECTED_SCHEDULES } from "store/scheduling.redux"
import { getId } from "utils/custom.utils"
import GenerateTripDialog from "../actions/GenerateTripDialog"
import DeleteSchedules from "../DeleteSchedules"
import ActionMessage from "./ActionMessage"
import useSchedulingStore from "../scheduling.store"
import { useScheduleListSummary } from "graphql/scheduling/scheduling.hooks"

export function NumberBadge({ selectedArray, intent, count }) {
  return (
    <>
      <CustomTag minimal intent={intent} round>
        {selectedArray ? selectedArray.length : count ? count : 0}
      </CustomTag>
    </>
  )
}

export default function SchedulingActions() {
  // Report Dialog States
  const [isGatepasOpen, toggleGatePass] = useState(false)
  const [isGenerateOpen, toggleGenerate] = useSchedulingStore(store => [
    store.showGenerateDialog,
    store.setShowGenerateDialog,
  ])
  const [isDeleteOpen, toggleDelete] = useState(false)
  const [isScheduleDownloadOpen, toggleScheduleDownload] = useState(false)
  const [isDetectionSheetOpen, toggleDetection] = useState(false)
  const [isEngagementReportOpen, toggleEngagement] = useState(false)

  // Permissions
  const disabled = useAuthorizationModules("add", "trip")
  const summary = useScheduleListSummary()
  const totalGeneratableTrips = summary?.count
  const dispatch = useDispatch()

  // GLOBAL State
  const date = useSelector(state => state.scheduling.selectedDate)
  const reportDate = dayjs(date).format("DDMMYYYY")
  const selectedSchedules = useSelector(
    state => state.scheduling.selectedSchedules
  )

  const selectedArray = useMemo(
    () => Object.keys(selectedSchedules),
    [selectedSchedules]
  )
  const [redirect] = useRedirect({
    date,
    schedule: selectedArray,
  })
  const validSchedules = useMemo(() => {
    return selectedArray.filter(s => !get(selectedSchedules, [s, "isNotified"]))
  }, [selectedSchedules, selectedArray])
  function handleClick(type) {
    switch (type) {
      case "known-vehicle":
        toggleGenerate(true)
        break
      case "download":
        toggleScheduleDownload(true)

        break
      case "document-share":
        toggleGatePass(true)

        break
      case "trash":
        toggleDelete(true)
        break
      case "detection":
        toggleDetection(true)
        // redirect(Links.DETECTION_SHEET);
        break
      case "engagement":
        toggleEngagement(true)
        break
      default:
        break
    }
  }
  return (
    <>
      <ActionMenu onClick={handleClick}>
        <MenuDivider title="Generate" />
        <ActionMenuItem
          disabled={disabled}
          icon="known-vehicle"
          text="Generate Trips"
          labelElement={
            <NumberBadge count={totalGeneratableTrips} intent="success" />
          }
        />
        <ActionMenuItem
          icon="document-share"
          text="Gate Pass"
          labelElement={
            <NumberBadge selectedArray={selectedArray} intent="primary" />
          }
        />
        <MenuDivider title="Report" icon="th" />
        <ActionMenuItem icon="download" text="Schedule" label=".xlsx" />
        <ActionMenuItem
          id="detection"
          icon="download"
          text="Detection"
          label=".xlsx"
        />
        <ActionMenuItem
          id="engagement"
          icon="download"
          text="Engagement"
          label=".xlsx"
        />
        <MenuDivider title="Actions" icon="th" />
        <ActionMenuItem
          labelElement={
            <NumberBadge selectedArray={validSchedules} intent="danger" />
          }
          disabled={validSchedules.length > 0 ? false : true}
          icon="trash"
          text="Delete Schedules"
        />
      </ActionMenu>
      <DeleteSchedules
        isOpen={isDeleteOpen}
        onClose={e => toggleDelete(false)}
        schedules={validSchedules}
      >
        <ActionMessage
          deletable={validSchedules}
          notDeletable={difference(selectedArray, validSchedules)}
        />
      </DeleteSchedules>
      <GenerateTripDialog
        validSchedules={validSchedules}
        schedules={selectedArray}
        isOpen={isGenerateOpen}
        onClose={e => toggleGenerate(false)}
      />
      <ReportDialog
        text="Gatepasses"
        title="Generate Gatepass"
        isOpen={isGatepasOpen}
        onClose={e => toggleGatePass(false)}
        onClick={({ depots }) => {
          redirect(Links.GATE_PASS, {
            dpts: depots?.map(d => getId(d)),
          })
          dispatch({
            type: CLEAR_SELECTED_SCHEDULES,
          })
        }}
      >
        <DynamicMultiSelector
          absolute
          label="Depots"
          query={DEPOTS}
          path="depots"
          fill
        />
      </ReportDialog>
      <ReportDialog
        title="Schedule"
        isOpen={isScheduleDownloadOpen}
        onClose={e => toggleScheduleDownload(false)}
        defaultData={{ dateRange: [reportDate, reportDate] }}
        onClick={({ dateRange, depots }) => {
          redirect(
            Links.SCHEDULE,
            {
              date: dateRange?.[0],
              till: dateRange?.[1],
              dpts: depots?.map(d => getId(d)),
            },
            true
          )
        }}
      >
        <DynamicMultiSelector
          absolute
          label="Depots"
          query={DEPOTS}
          path="depots"
          fill
        />
        <CustomDateRange
          fill
          fixed
          big={false}
          shortcuts={true}
          path="dateRange"
          label="Date Range"
          valueAs="DDMMYYYY"
        />
      </ReportDialog>
      <ReportDialog
        title="Detection Sheet"
        isOpen={isDetectionSheetOpen}
        onClose={e => toggleDetection(false)}
        defaultData={{ dateRange: [reportDate, reportDate] }}
        onClick={({ dateRange, markets }) => {
          redirect(
            Links.DETECTION_SHEET,
            {
              date: dateRange?.[0],
              till: dateRange?.[1],
              mrkts: markets?.map(m => m.codename),
            },
            true
          )
        }}
      >
        <DynamicMultiSelector
          fill
          absolute
          className="mb-3"
          path="markets"
          label="Markets"
          query={MARKETS}
        />
        <CustomDateRange
          fill
          fixed
          shortcuts={true}
          big={false}
          path="dateRange"
          label="Date Range"
          valueAs="DDMMYYYY"
        />
      </ReportDialog>
      <ReportDialog
        title="Engagement Report"
        isOpen={isEngagementReportOpen}
        onClose={e => toggleEngagement(false)}
        defaultData={{ dateRange: [reportDate, reportDate] }}
        onClick={({ dateRange }) => {
          redirect(
            Links.ENGAGEMENT_REPORT,
            {
              date: dateRange?.[0],
              till: dateRange?.[1],
            },
            true
          )
        }}
      >
        <CustomDateRange
          fill
          fixed
          shortcuts={true}
          big={false}
          path="dateRange"
          label="Date Range"
          valueAs="DDMMYYYY"
        />
      </ReportDialog>
    </>
  )
}
